/**
 * The quiet-spell clock.
 *
 * How long since anybody touched the application, in any tab of this
 * browser, and what a tab should do about it. The moment of the last touch
 * is kept in localStorage, so a teller working in one tab keeps every tab
 * signed in and a tab left open in the background counts the same silence
 * as the one in front.
 *
 * Nothing here sets a timer or reads an event. A tab asks what stage it is
 * at and how long until the next one, and acts on the answer; that keeps
 * the arithmetic in one place where it can be tested with a clock in hand,
 * and it keeps the tab honest when its timers run late, as they do in a
 * background tab: the stage is always worked out from the stamp, never
 * from how long a timer thought it waited.
 */

export const ACTIVITY_KEY = "hrsys.auth.last-activity";

/** How long before the limit the tab offers to stay signed in. */
export const WARNING_MS = 60_000;

/**
 * How close together two touches must be before the second is not written
 * down. A pointer crossing the screen fires hundreds of moves; one stamp a
 * second is more than the clock needs.
 */
export const STAMP_EVERY_MS = 1_000;

/** The events that count as somebody being there. */
export const ACTIVITY_EVENTS = [
  "pointerdown",
  "pointermove",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
] as const;

export type IdleStage = "active" | "warning" | "expired";

export interface IdlePlan {
  stage: IdleStage;
  /** How long until the stage changes, if nobody touches anything. */
  wait: number;
  /** How long until the tab signs out, if nobody touches anything. */
  remaining: number;
}

/**
 * Where a tab stands, given when it was last touched and how long it may go.
 *
 * The warning begins a minute before the limit. A limit of a minute or less
 * has no room for one, so the tab goes from active straight to expired
 * rather than warning the moment anybody stops.
 */
export function planIdle(lastActivity: number, now: number, limitMs: number): IdlePlan {
  const idleFor = Math.max(0, now - lastActivity);
  const remaining = Math.max(0, limitMs - idleFor);
  if (remaining === 0) return { stage: "expired", wait: 0, remaining: 0 };

  const warnAt = limitMs - WARNING_MS;
  if (warnAt > 0 && idleFor >= warnAt) {
    return { stage: "warning", wait: remaining, remaining };
  }
  const nextStage = warnAt > 0 ? warnAt : limitMs;
  return { stage: "active", wait: nextStage - idleFor, remaining };
}

/**
 * The newest stamp this page has made, for when storage cannot be read.
 * Also the floor under what storage says: this tab cannot have been touched
 * earlier than it last wrote.
 */
let inMemory = 0;

export function readLastActivity(fallback: number): number {
  let stored = NaN;
  try {
    const raw = localStorage.getItem(ACTIVITY_KEY);
    stored = raw === null ? NaN : Number(raw);
  } catch {
    // Blocked storage: the tab keeps its own count below.
  }
  if (Number.isFinite(stored)) return Math.max(stored, inMemory);
  return inMemory || fallback;
}

/**
 * Writes down that somebody is here. Returns whether it was written: a
 * touch within a second of the last is not, and the caller need not
 * reschedule anything for it.
 *
 * A stamp from the future, after a clock put back, would otherwise hold the
 * clock still until the time caught up; it is simply overwritten.
 */
export function recordActivity(now: number = Date.now()): boolean {
  // A pointer in motion asks many times a second; this tab's own last stamp
  // answers most of them without going to storage.
  if (inMemory <= now && now - inMemory < STAMP_EVERY_MS) return false;
  const last = readLastActivity(0);
  if (last <= now && now - last < STAMP_EVERY_MS) return false;
  inMemory = now;
  try {
    localStorage.setItem(ACTIVITY_KEY, String(now));
  } catch {
    // Blocked storage: this tab still knows, the others count for themselves.
  }
  return true;
}

/** Test seam: a fresh page has made no stamp. */
export function resetActivityMemory(): void {
  inMemory = 0;
}
